/**
 * Report Label Translations
 * =========================
 *
 * Mirror of the backend `report_label_translations.py` dictionary —
 * locked translations of structural report labels (section titles, JSON
 * keys, analysis sub-labels) that must always render identically for a
 * given language. No LLM is ever consulted for these strings.
 *
 * Keep this file in sync with the Python source on the backend. The
 * canonical mapping lives in:
 *
 *   Reporting-Backend-app/app/modules/reports/services/report_label_translations.py
 *
 * If the Python dict changes, update this file by hand or by running
 * `dump_translations_json()` on the backend and pasting the output.
 *
 * Usage in components:
 *
 *   import { label } from '@/config/reportLabelTranslations';
 *   const lang = 'fr';
 *   <h3>{label('Session at a Glance', lang)}</h3>
 *   // -> "Aperçu de la session"
 */

export const LABEL_TRANSLATIONS = {
  fr: {
    'Session at a Glance': 'Aperçu de la session',
    'Summary': 'Résumé',
    'Key Points': 'Points clés',
    'Full Session Report': 'Compte rendu de la session',
    'Speakers and Arguments': 'Intervenant(e)s et Arguments',
    'Speakers': 'Intervenant(e)s',
    'In-Depth Analysis': 'Analyse Approfondie',
    'Key Takeaways': 'Points clés',
    'Follow-up Questions': 'Questions de suivi',
    'Explanation': 'Explication',
    'Evidence': 'Preuve à l\u2019appui',
    'Major discussion point': 'Point de discussion principal',
    'Topics': 'Sujets',
    'Arguments': 'Arguments',
    'Agreements': 'Ententes',
    'Agreement points': 'Points d\u2019accord',
    'Similar viewpoints': 'Points de vue similaires',
    'Unexpected consensus': 'Consensus inattendu',
    'Overall assessment': 'Évaluation globale',
    'Consensus level': 'Niveau de consensus',
    'Differences': 'Divergences',
    'Disagreement level': 'Niveau de désaccord',
    'Partial Agreements': 'Ententes partielles',
    'Resolutions and action items': 'Résolutions et mesures à prendre',
    'Unresolved issues': 'Problèmes non résolus',
    'Suggested compromises': 'Compromis proposés',
    'Thought-Provoking Comments': 'Commentaires stimulants',
  },
};

/**
 * Translate a canonical English report label to `lang`.
 *
 * Returns the input text unchanged when `lang` is empty / 'en' /
 * unknown, or when the specific key has no translation. This mirrors
 * the permissive behavior of the Python `label()` helper so partial
 * dictionaries stay safe to ship.
 *
 * @param {string} text - The English label as it appears in the codebase.
 * @param {string} [lang='en'] - ISO 639-1 language code.
 * @returns {string} Translated label or original text.
 */
export function label(text, lang = 'en') {
  if (!text || !lang) return text;
  const code = String(lang).toLowerCase().trim();
  if (!code || code === 'en') return text;
  const bucket = LABEL_TRANSLATIONS[code];
  if (!bucket) return text;
  return bucket[text] ?? text;
}

/**
 * List language codes that have at least a partial dictionary.
 * @returns {string[]}
 */
export function supportedLanguages() {
  return Object.keys(LABEL_TRANSLATIONS).sort();
}
